import React, { useState, useEffect, useContext } from "react";
import {
  Box, Grid, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, FormControl, InputLabel, Select, MenuItem, OutlinedInput,
  Checkbox, CircularProgress,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import darkTheme from "../../style/theme";
import DashboardBreadcrumbs from "../../components/DashboardBreadcrumbs";
import { Icon as OpusIcon, EmptyState, CardGridSkeleton, Toggle as OpusToggle, Button as OpusButton, IconButton as OpusIconButton } from "../../components/common/opus";
import { useQuickLinks } from "../../hooks/useQuickLinks";
import { useSession } from "../../contexts/SessionContext";
import { AuthContext } from "../../contexts/AuthContext";
import { usePermissions } from "../../contexts/PermissionsContext";
import { useAlert } from "../../contexts/AlertContext.js";
import { useConfirm } from "../../components/common/ConfirmDialog";
import apiService from "../../services/api.service";

// OPUS-1045: "My Apps" is the single place to BOTH launch and manage org apps.
// The launcher reuses the OPUS-901/902 handoff flow (useQuickLinks); the editor
// (add/edit/delete) was moved here from the retired Settings page and is gated
// by the same `dashboard.settings.view` permission. Edits target the currently
// selected org (the sidebar org switcher), so super admins manage any org by
// switching to it. Mutations call bumpCtas() to refresh the list in place.
const AVAILABLE_PARAMS = [
  { value: "userId", label: "User ID" },
  { value: "username", label: "Username" },
  { value: "orgId", label: "Organization ID" },
  { value: "email", label: "Email" },
  { value: "role", label: "Role" },
];
const EMPTY_FORM = { name: "", seq: 0, logo: "", href: "", isLocalEndpoint: false, urlParams: [], target_org_id: "", visibility: "org" };

const MyApps = () => {
  const { ctas, loading, launch } = useQuickLinks();
  const { selectedOrgId, bumpCtas } = useSession();
  const { userInfo } = useContext(AuthContext);
  const { can } = usePermissions();
  const alert = useAlert();
  const confirm = useConfirm();

  const userOrg = userInfo?.data?.organization_id?._id;
  const org = selectedOrgId || userOrg; // the org being viewed/edited
  const isSuperAdmin = userInfo?.data?.role?.name === "super_admin";
  const canEdit = can("dashboard.settings.view"); // same permission as the old Settings page

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCTA, setEditingCTA] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [organizations, setOrganizations] = useState([]); // super-admin target-org picker

  useEffect(() => {
    if (!isSuperAdmin) return;
    (async () => {
      try {
        const resp = await apiService.getOrganizations();
        const list = resp?.data || resp || [];
        setOrganizations(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("Failed to load organisations for target picker:", e);
      }
    })();
  }, [isSuperAdmin]);

  const openDialog = (cta = null) => {
    if (cta) {
      setEditingCTA(cta);
      setFormData({
        name: cta.name || "", seq: cta.seq || 0, logo: cta.logo || "", href: cta.href || "",
        isLocalEndpoint: cta.isLocalEndpoint || false, urlParams: cta.urlParams || [],
        target_org_id: cta.target_org_id || "", visibility: cta.visibility || "org",
      });
    } else {
      setEditingCTA(null);
      const nextSeq = ctas.length > 0 ? Math.max(...ctas.map((c) => c.seq || 0)) + 1 : 1;
      setFormData({ ...EMPTY_FORM, seq: nextSeq });
    }
    setDialogOpen(true);
  };

  const closeDialog = () => { setDialogOpen(false); setEditingCTA(null); setFormData(EMPTY_FORM); };

  const handleSave = async () => {
    if (!formData.name || !formData.href) { alert.error("Name and href are required"); return; }
    if (!org) { alert.error("No organisation selected"); return; }
    try {
      setSaving(true);
      if (editingCTA) {
        await apiService.updateCTA(org, editingCTA._id, formData);
        alert.success("App updated successfully");
      } else {
        await apiService.addCTA(org, formData);
        alert.success("App added successfully");
      }
      closeDialog();
      bumpCtas(); // refreshes useQuickLinks (this list) immediately
    } catch (error) {
      console.error("Error saving app:", error);
      alert.error(error.message || "Failed to save app");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cta) => {
    if (!(await confirm({ title: "Delete app?", message: `Remove "${cta.name}" from this organisation's apps?`, destructive: true }))) return;
    try {
      await apiService.deleteCTA(org, cta._id);
      alert.success("App deleted successfully");
      bumpCtas();
    } catch (error) {
      console.error("Error deleting app:", error);
      alert.error(error.message || "Failed to delete app");
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <Grid container spacing={1} sx={{ height: "100vh" }}>
        <Grid size={12}>
          <DashboardBreadcrumbs
            trail={[{ label: "My Apps" }]}
            rightAction={canEdit ? (
              <OpusButton variant="primary" size="sm" icon="plus" onClick={() => openDialog()}>Add App</OpusButton>
            ) : null}
          />
        </Grid>

        <Grid size={12} sx={{ height: "100%" }}>
          <Box
            className="opus-scroll"
            sx={{
              height: "calc(100% - 67px)", overflowY: "auto", p: 2,
              border: "1px solid var(--border)", borderRadius: "var(--r-lg)", background: "var(--surface-1)",
            }}
          >
            <Typography sx={{ fontSize: 14, color: "var(--text-muted)", mb: 2 }}>
              {canEdit ? "Launch or manage the apps configured for your organisation." : "Launch the apps configured for your organisation."}
            </Typography>

            {loading ? (
              <CardGridSkeleton count={8} minWidth={220} preview={false} label="Loading your apps…" />
            ) : ctas.length === 0 ? (
              <EmptyState
                icon="layers"
                title="No apps yet"
                message={canEdit ? 'Click "Add App" to configure a launcher for your organisation.' : "Apps configured for your organisation will appear here."}
              />
            ) : (
              <Grid container spacing={2}>
                {ctas.map((cta, index) => (
                  <Grid key={cta._id || index} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => launch(cta)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); launch(cta); } }}
                      sx={{
                        position: "relative", display: "flex", alignItems: "center", gap: 1.5, p: 2, height: "100%",
                        cursor: "pointer", border: "1px solid var(--border)", borderRadius: "var(--r-md)", background: "var(--surface-0)",
                        transition: "border-color .13s, background .13s, transform .13s",
                        "&:hover": { borderColor: "var(--coral)", background: "var(--surface-2)", transform: "translateY(-2px)" },
                        "&:hover .myapps-edit": { opacity: 1 },
                        "&:focus-visible": { outline: "2px solid var(--coral)", outlineOffset: 2 },
                      }}
                    >
                      <Box
                        sx={{
                          width: 44, height: 44, flexShrink: 0, borderRadius: "var(--r-md)", display: "flex",
                          alignItems: "center", justifyContent: "center", overflow: "hidden",
                          background: cta.logo ? "transparent" : "var(--coral)", color: "#fff", fontSize: 18, fontWeight: 700,
                        }}
                      >
                        {cta.logo ? (
                          <Box component="img" src={cta.logo} alt={cta.name} sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
                        ) : ((cta.name || "?").charAt(0).toUpperCase())}
                      </Box>

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography noWrap sx={{ fontSize: 15, fontWeight: 600, color: "var(--text)" }}>{cta.name}</Typography>
                        {cta.href && (
                          <Typography noWrap sx={{ fontSize: 12, color: "var(--text-dim)", fontFamily: "var(--font-mono)" }}>{cta.href}</Typography>
                        )}
                      </Box>

                      {canEdit ? (
                        <Box
                          className="myapps-edit"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ display: "flex", gap: 0.25, opacity: 0, transition: "opacity .12s" }}
                        >
                          <OpusIconButton icon="edit" size={15} title="Edit" onClick={() => openDialog(cta)} />
                          <OpusIconButton icon="trash" size={15} danger title="Remove" onClick={() => handleDelete(cta)} />
                        </Box>
                      ) : (
                        <OpusIcon name="external" size={16} />
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Add/Edit App dialog — moved from the retired Settings page (OPUS-1045). */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCTA ? "Edit App" : "Add New App"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <TextField fullWidth label="Name" value={formData.name} required
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label="Sequence" type="number" value={formData.seq}
                helperText="Order in which the app appears"
                onChange={(e) => setFormData({ ...formData, seq: parseInt(e.target.value, 10) || 0 })} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label="Logo URL" value={formData.logo} helperText="URL or path to logo image"
                onChange={(e) => setFormData({ ...formData, logo: e.target.value })} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label="Href" value={formData.href} required helperText="Link URL or local endpoint path"
                onChange={(e) => setFormData({ ...formData, href: e.target.value })} />
            </Grid>
            <Grid size={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <OpusToggle checked={formData.isLocalEndpoint} onChange={(v) => setFormData({ ...formData, isLocalEndpoint: v })} />
                <Typography variant="body2">Is Local Endpoint</Typography>
              </Box>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>URL Parameters</InputLabel>
                <Select
                  multiple
                  value={formData.urlParams || []}
                  onChange={(e) => setFormData({ ...formData, urlParams: e.target.value })}
                  input={<OutlinedInput label="URL Parameters" />}
                  renderValue={(selected) => selected.map((val) => AVAILABLE_PARAMS.find((p) => p.value === val)?.label || val).join(", ")}
                >
                  {AVAILABLE_PARAMS.map((param) => (
                    <MenuItem key={param.value} value={param.value}>
                      <Checkbox checked={formData.urlParams?.indexOf(param.value) > -1} />
                      {param.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                Parameters appended to the URL from user data on launch.
              </Typography>
            </Grid>
            <Grid size={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <OpusToggle
                  checked={formData.visibility === "private"}
                  onChange={(v) => setFormData({ ...formData, visibility: v ? "private" : "org", ...(v ? { target_org_id: "" } : {}) })}
                />
                <Typography variant="body2">Show only to me</Typography>
              </Box>
            </Grid>
            {isSuperAdmin && formData.visibility !== "private" && (
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel>Target organisation</InputLabel>
                  <Select value={formData.target_org_id || ""} label="Target organisation"
                    onChange={(e) => setFormData({ ...formData, target_org_id: e.target.value })}>
                    <MenuItem value="">This organisation</MenuItem>
                    {organizations.filter((o) => String(o._id) !== String(org)).map((o) => (
                      <MenuItem key={o._id} value={o._id}>{o.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : editingCTA ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default MyApps;
